import { useCallback, useEffect, useMemo, useRef } from 'react'
import { vclog } from '@/features/meeting/hooks/vc-log'
import type { ChatAttachment, ChatMessage, ChatThreadId } from '../models/chat.model'
import { createChannelTransport, type ChatChannel } from '../services/chat.service'
import { fetchMeetingChatHistory } from '../services/meeting-chat.api'
import { selectTotalUnread, useChatStore } from '../store/chat.store'

/**
 * In-meeting chat, on the room channel.
 *
 * Send goes out on the channel and comes back through the same broadcast every
 * other participant receives — there is no local echo. That is deliberate: one
 * delivery path means the message you see is the one the room saw, with the
 * server's id and timestamp, and no reconciliation between an optimistic copy
 * and the real one.
 *
 * Without a channel (none until the socket joins) the hook is inert rather than
 * fake: sends are dropped instead of quietly appending to local state, which is
 * what the previous mock did — and why meeting chat looked like it worked while
 * reaching nobody at all.
 */

type UseChatOptions = {
  /** Live channel handles from `usePhoenixChannel`. Null until joined. */
  channel?: ChatChannel | null
  /** Signaling room id — needed to fetch the archive. */
  roomId?: string | null
  /** Phoenix room token; the credential the history endpoint accepts. */
  roomToken?: string | null
  /**
   * A live message from someone else that landed unread — the thread it belongs
   * to is not the one on screen. Where the meeting raises its chat toast.
   */
  onUnreadMessage?: (msg: ChatMessage) => void
}

let _localId = 0
const nextLocalId = () => `mc-${Date.now().toString(36)}-${++_localId}`

export function useChat(
  localUserId: string,
  localUserName: string,
  options: UseChatOptions = {},
) {
  const { channel = null, roomId = null, roomToken = null, onUnreadMessage } = options
  const onUnreadRef = useRef(onUnreadMessage)
  onUnreadRef.current = onUnreadMessage

  const group = useChatStore((s) => s.group)
  const privateThreads = useChatStore((s) => s.private)
  const unreadGroup = useChatStore((s) => s.unreadGroup)
  const unreadPrivate = useChatStore((s) => s.unreadPrivate)
  const typingUsers = useChatStore((s) => s.typingUsers)
  const historyLoaded = useChatStore((s) => s.historyLoaded)
  const unreadCount = useChatStore(selectTotalUnread)

  const setRoomIdStore = useChatStore((s) => s.setRoomId)
  const receiveMessage = useChatStore((s) => s.receiveMessage)
  const loadHistory = useChatStore((s) => s.loadHistory)
  const setActiveThreadStore = useChatStore((s) => s.setActiveThread)
  const markThreadReadStore = useChatStore((s) => s.markThreadRead)
  const markAllReadStore = useChatStore((s) => s.markAllRead)
  const clearThreadStore = useChatStore((s) => s.clearThread)
  const clearAllStore = useChatStore((s) => s.clearAll)
  const setTypingStore = useChatStore((s) => s.setTyping)

  const transport = useMemo(() => (channel ? createChannelTransport(channel) : null), [channel])

  // The store actions need the local id to decide thread + ownership; keeping
  // it in a ref means the subscription doesn't tear down when the id resolves.
  const selfRef = useRef(localUserId)
  selfRef.current = localUserId

  // Inbound messages.
  //
  // Logged because the unread badge is derived state with three inputs (who
  // sent it, which thread it belongs to, which thread is on screen) and a wrong
  // count tells you nothing about which of the three was wrong.
  useEffect(() => {
    if (!transport) return
    return transport.subscribe((msg) => {
      const unreadBefore = selectTotalUnread(useChatStore.getState())
      receiveMessage(msg, selfRef.current)
      const s = useChatStore.getState()
      // The store already decides what is unread (not yours, not the thread on
      // screen, not a duplicate); a message that raised the count is one the
      // user has not seen.
      if (selectTotalUnread(s) > unreadBefore) onUnreadRef.current?.(msg)
      vclog('socket', 'chat rx', {
        id: msg.id,
        from: msg.senderId,
        to: msg.to ?? null,
        self: selfRef.current,
        own: msg.senderId === selfRef.current,
        activeThread: s.activeThread,
        unreadGroup: s.unreadGroup,
        unreadPrivate: s.unreadPrivate,
        total: selectTotalUnread(s),
      })
    })
  }, [transport, receiveMessage])

  // Dev-only handle so the live store can be inspected from the console:
  //   __chatStore.getState()
  useEffect(() => {
    if (!import.meta.env.DEV) return
    ;(window as unknown as Record<string, unknown>).__chatStore = useChatStore
  }, [])

  // Inbound typing. Only one indicator list is rendered today; a private
  // "typing" still reaches only the peer it was addressed to, server-side.
  useEffect(() => {
    if (!transport) return
    return transport.subscribeTyping(({ from, fromName, typing }) => {
      if (from === selfRef.current) return
      setTypingStore(fromName, typing)
    })
  }, [transport, setTypingStore])

  // The store is module-global and outlives any one meeting, so a second
  // meeting in the same tab would open showing the first one's threads and its
  // unread badges. Reset when the room changes — before the history effect
  // below, which relies on `historyLoaded` going back to false so the new
  // room's archive is actually fetched.
  //
  // The room is remembered in the store, not in a ref. A ref dies with the
  // meeting screen, so leaving one meeting and starting another mounted a fresh
  // ref that had never seen the first room: nothing was cleared, and the new
  // meeting opened with the old one's messages and files.
  useEffect(() => {
    if (roomId) setRoomIdStore(roomId)
  }, [roomId, setRoomIdStore])

  // Archive, once per join. Live messages arriving meanwhile are deduped by id
  // in the store, so the order of the two doesn't matter.
  useEffect(() => {
    if (!roomId || !roomToken || historyLoaded || !localUserId) return
    let cancelled = false
    void fetchMeetingChatHistory(roomId, roomToken).then((msgs) => {
      // Flagged loaded even when empty, so a chat-less meeting doesn't refetch
      // on every render.
      if (!cancelled) loadHistory(msgs, localUserId)
    })
    return () => {
      cancelled = true
    }
  }, [roomId, roomToken, historyLoaded, localUserId, loadHistory])

  const sendMessage = useCallback(
    (text: string, to?: string | null, attachment?: ChatAttachment | null) => {
      const trimmed = text.trim()
      if (!trimmed && !attachment) return
      if (!transport) return
      transport.publish({ id: nextLocalId(), text: trimmed, to: to ?? null, attachment })
    },
    [transport],
  )

  const sendTyping = useCallback(
    (typing: boolean, to?: string | null) => transport?.publishTyping(typing, to ?? null),
    [transport],
  )

  const setActiveThread = useCallback(
    (thread: ChatThreadId | null) => {
      setActiveThreadStore(thread)
      const s = useChatStore.getState()
      vclog('socket', 'chat thread active', {
        thread,
        unreadGroup: s.unreadGroup,
        unreadPrivate: s.unreadPrivate,
        total: selectTotalUnread(s),
      })
    },
    [setActiveThreadStore],
  )
  const markThreadRead = useCallback(
    (thread: ChatThreadId) => markThreadReadStore(thread),
    [markThreadReadStore],
  )
  const markAllRead = useCallback(() => markAllReadStore(), [markAllReadStore])
  const clearThread = useCallback(
    (thread: ChatThreadId) => clearThreadStore(thread),
    [clearThreadStore],
  )
  const clearAll = useCallback(() => clearAllStore(), [clearAllStore])
  const setTyping = useCallback(
    (userName: string, isTyping: boolean) => setTypingStore(userName, isTyping),
    [setTypingStore],
  )

  return {
    /** Group thread. */
    messages: group,
    /** peerId → that private conversation. */
    privateThreads,
    unreadGroup,
    unreadPrivate,
    /** Every thread's unread, summed — the control-bar badge. */
    unreadCount,
    typingUsers,
    /** Chat is only live once the channel exists. */
    connected: !!transport,
    localUserId,
    localUserName,
    sendMessage,
    sendTyping,
    setActiveThread,
    markThreadRead,
    markAllRead,
    clearThread,
    clearAll,
    setTyping,
  }
}
